// The "Models" tour — custom ML as its own first-class surface, and (the part
// that actually matters) how to READ a model rather than just produce one.
// Global: every project gets /models, so this tour is offered everywhere.
//
// TARGETING CONSTRAINT that shapes this tour: the overlay blocks page
// interaction, so the tour cannot open a model's detail modal. Anything that
// lives only inside that modal — the metrics block, the importance bars, the
// version list, the score button — therefore CANNOT be spotlighted, and a step
// pointing at it would stall for waitMs and then silently drop to a centered
// card anyway (TutorialFlow: target not found within waitMs → centered).
// So those concepts are taught with deliberate centered cards, the same way
// agenticSources explains the create modal. Only page-level anchors that exist
// on an empty /models are used as targets — which also means the tour works on a
// project with no models yet, the state a first-time reader is actually in.
//
// The spine is that a model is only as trustworthy as four things: a
// cross-validated score, the data commit behind it, the features driving it, and
// a version you can roll back. Anyone who trains one without reading those has a
// number, not a model.

export const modelsTour = {
  key: "models",
  title: "Models (custom ML)",
  description:
    "Train a custom model on your governed data — and learn to read the four things that decide whether you can trust it: the cross-validated score, the data commit behind it, the features driving it, and the version history.",
  steps: [
    {
      title: "Models are not agents",
      body:
        "Both get called “AI”, which is exactly why cortex keeps them apart.<br/><br/>" +
        "An <b>agent</b> is an LLM persona: it decides what to do at run time, and the same " +
        "question can get a different answer. A <b>model</b> is a trained artifact: the same " +
        "input gives the same output, forever, until you deliberately replace it.<br/><br/>" +
        "That difference forces four things agents have no concept of — a <b>version " +
        "history</b>, a <b>measured score</b>, the <b>lineage</b> of the data it learned from, " +
        "and the <b>features</b> that drive its predictions. This page is built around those four.",
    },
    {
      page: "models",
      target: ['[data-tour="models-page"]', "main"],
      title: "The Models page",
      body:
        "Models uses the same explorer layout as Apps and Agents: the project's models on the " +
        "left, the one you pick on the right.<br/><br/>" +
        "Each row carries the number that matters — its <b>ROC-AUC</b>, how well the model " +
        "separates the two outcomes. 0.5 is a coin flip, 1.0 is perfect. Open a model and the " +
        "pane shows its metrics, the data commit that trained it, what drives its predictions, " +
        "and its full version history.",
    },
    {
      target: ['[data-tour="models-rail"]', "main"],
      title: "Step 1 — get a model",
      body:
        "The rail lists this project's models, exactly like Apps and Agents. Two ways to add one:" +
        "<br/><br/>" +
        "<b>Build a model</b> files a REQUEST — describe the outcome you want predicted in plain " +
        "language and an agent builds it, with the result tracked on your Board. Or ask the " +
        "<b>Models Assistant</b> in chat.<br/><br/>" +
        "<b>New model, manually</b> is the shortcut when you already know your query: it asks for " +
        "a name and the <b>column to predict</b> — a 0/1 column such as <code>abnormal</code>, " +
        "<code>churned</code> or <code>failed</code>. That only registers the model; nothing is " +
        "trained yet and no data has been touched.",
      placement: "right",
    },
    {
      title: "Step 2 — the training query IS the dataset",
      body:
        "Open the model and press <b>Train a version</b>. There is no upload step and no CSV: " +
        "you write a <b>SELECT</b> over your own governed tables.<br/><br/>" +
        "It must return the column to predict, plus every column the model should learn from. " +
        "For example, over sensor readings joined to their asset:<br/>" +
        "<code>select r.reading_id, r.reading_value, r.baseline_value,<br/>" +
        "&nbsp;&nbsp;(r.reading_value/nullif(r.baseline_value,0)) as ratio,<br/>" +
        "&nbsp;&nbsp;r.sensor_type, a.criticality, a.health_score,<br/>" +
        "&nbsp;&nbsp;case when r.reading_status in ('warning','alarm')<br/>" +
        "&nbsp;&nbsp;&nbsp;&nbsp;then 1 else 0 end as abnormal<br/>" +
        "from sensor_readings r join assets a on a.asset_id = r.reading_asset_id</code>" +
        "<br/><br/>" +
        "Because it is a live query, the training set is always current governed data — never " +
        "a stale export someone made months ago.",
    },
    {
      title: "Step 3 — name the features",
      body:
        "Two boxes split the columns by type. <b>Numeric features</b> are measurements " +
        "(<code>reading_value</code>, <code>health_score</code>). <b>Category features</b> are " +
        "labels (<code>sensor_type</code>, <code>criticality</code>).<br/><br/>" +
        "The engine fills in missing values, encodes the categories, holds back a quarter of " +
        "the rows to score itself honestly, and trains. The <b>first column of your query is " +
        "the row id</b> — it identifies each scored row and is not learned from.<br/><br/>" +
        "Training a few hundred rows takes seconds.",
    },
    {
      title: "Reading the score — trust the CV number",
      body:
        "Two scores come back and they are <i>not</i> equally trustworthy.<br/><br/>" +
        "<b>Holdout</b> is one split of the data. On a small dataset it flatters, sometimes " +
        "wildly. <b>Cross-validated (CV)</b> repeats the split several times and reports the " +
        "average with a <b>± spread</b>.<br/><br/>" +
        "Trust the CV figure and read the spread: <code>0.97 ± 0.06</code> is solid, while " +
        "<code>0.95 ± 0.20</code> swings badly depending on which rows it trained on. If the " +
        "holdout is higher than the CV, cortex says so on the page.<br/><br/>" +
        "A perfect <code>1.000</code> is a warning sign, not a triumph — the next step explains why.",
    },
    {
      title: "What drives the prediction — and spotting leakage",
      body:
        "The active version shows bars for how much each feature actually moves the " +
        "prediction. Read them for two reasons.<br/><br/>" +
        "First, a score nobody can explain gets ignored by whoever has to act on it — " +
        "“this pump scored 0.91” is only useful if you can see <i>which readings</i> caused it." +
        "<br/><br/>" +
        "Second, and this is why the bars are on the page: if a single feature carries almost " +
        "all the weight, that usually means <b>leakage</b> — the model found a column that " +
        "already encodes the answer, so it looks brilliant here and fails in production. " +
        "Cortex flags that case for you with a warning under the bars.",
    },
    {
      title: "Lineage — which data made this model",
      body:
        "Every version permanently records the <b>commit hash</b> of the governed data it " +
        "learned from, shown as “Trained from recipe commit …”.<br/><br/>" +
        "That answers a question most ML tools cannot: <i>which data made this model, and " +
        "which model made this prediction?</i> In a product whose whole premise is governed, " +
        "auditable data, a model you cannot trace is a contradiction — so the pin is not " +
        "optional and cannot be added later.",
    },
    {
      title: "Versions, promotion and rollback",
      body:
        "Training again makes a new version; nothing is ever overwritten. One version is " +
        "<b>Active</b> — the one that scores.<br/><br/>" +
        "<b>Promote</b> makes a different version active, and rolling back is the same click " +
        "on an older row. Because promotion is a single switch, a bad model is never a " +
        "destructive event. Failed attempts stay in the list too, with the reason they failed, " +
        "so the history tells the whole story.",
    },
    {
      title: "Scoring your data",
      body:
        "<b>Score all rows</b> runs the active version across your data and writes the results " +
        "— a score, a 0/1 label, and the <b>model version that produced them</b> — back into a " +
        "governed table (<code>ml_prediction</code>), where any app, dashboard or workflow can " +
        "read them.<br/><br/>" +
        "Storing the version alongside each prediction is what keeps “why was this flagged in " +
        "March?” answerable long after the model has moved on.",
    },
    {
      target: ['[data-tour="models-list"]', '[data-tour="models-empty"]', "main"],
      title: "Where to go from here",
      body:
        "That is the whole loop: <b>create → query → train → read the score, the features and " +
        "the lineage → promote → score</b>.<br/><br/>" +
        "Today a model scores your data in <b>batches</b>, which fits questions like “which of " +
        "these assets need attention?”. Scoring a single record <i>inside</i> a running " +
        "business process — mid-workflow, while someone waits — is the next step, and " +
        "measurements already show it responding in about <b>10&nbsp;ms</b> once warm, so it " +
        "is well within reach.",
    },
  ],
};
